import { useState } from "react";
import { Zap } from "lucide-react";

interface TokenAllocation {
  label: string;
  percentage: number;
  color: string;
  description: string;
}

const allocations: TokenAllocation[] = [
  {
    label: "Airdrop",
    percentage: 50,
    color: "#8B5CF6",
    description: "500M tokens distributed to community members",
  },
  {
    label: "Presale",
    percentage: 20,
    color: "#A78BFA",
    description: "200M tokens available during 48-hour presale",
  },
  {
    label: "Liquidity Pool",
    percentage: 20,
    color: "#C4B5FD",
    description: "200M tokens paired with $50k initial LP",
  },
  {
    label: "Team & Marketing",
    percentage: 10,
    color: "#DDD6FE",
    description: "100M tokens vested for 1 year",
  },
];

function calculatePath(
  percentage: number,
  startAngle: number,
  radius: number,
  centerX: number,
  centerY: number
): string {
  const angle = (percentage / 100) * 360;
  const endAngle = startAngle + angle;
  
  const startAngleRad = (startAngle * Math.PI) / 180;
  const endAngleRad = (endAngle * Math.PI) / 180;
  
  const x1 = centerX + radius * Math.cos(startAngleRad);
  const y1 = centerY + radius * Math.sin(startAngleRad);
  const x2 = centerX + radius * Math.cos(endAngleRad);
  const y2 = centerY + radius * Math.sin(endAngleRad);
  
  const largeArcFlag = angle > 180 ? 1 : 0;
  
  return `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
}

export function TokenomicsChart() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const radius = 120;
  const centerX = 150;
  const centerY = 150;

  let currentAngle = -90; // Start from top

  return (
    <div className="w-full py-8">
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
          {/* Pie Chart */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[300px] mx-auto lg:mx-0">
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 300 300"
                className="drop-shadow-2xl"
                style={{ aspectRatio: "1/1" }}
              >
                {allocations.map((allocation, index) => {
                  const path = calculatePath(
                    allocation.percentage,
                    currentAngle,
                    radius,
                    centerX,
                    centerY
                  );
                  const isHovered = hoveredIndex === index;
                  const isSelected = selectedIndex === index;
                  
                  currentAngle += (allocation.percentage / 100) * 360;

                  return (
                    <path
                      key={index}
                      d={path}
                      fill={allocation.color}
                      stroke="#1F2937"
                      strokeWidth="2"
                      className="transition-all duration-300 cursor-pointer"
                      style={{
                        opacity: hoveredIndex !== null && hoveredIndex !== index ? 0.5 : 1,
                        transform: isHovered || isSelected ? "scale(1.05)" : "scale(1)",
                        transformOrigin: "150px 150px",
                      }}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
                    />
                  );
                })}
                
                {/* Center circle with logo */}
                <circle
                  cx={centerX}
                  cy={centerY}
                  r="60"
                  fill="#1F2937"
                  className="transition-all duration-300"
                  style={{
                    transform: hoveredIndex !== null ? "scale(1.1)" : "scale(1)",
                  }}
                />
                <foreignObject x="120" y="120" width="60" height="60">
                  <div className="flex items-center justify-center h-full">
                    <Zap className="w-8 h-8 text-purple-400" fill="currentColor" />
                  </div>
                </foreignObject>
              </svg>
              
              {/* Glow effect */}
              <div className="absolute inset-0 -z-10 bg-gradient-to-r from-purple-500/20 to-violet-500/20 rounded-full blur-3xl" />
            </div>
          </div>

          {/* Legend */}
          <div className="space-y-3 md:space-y-4">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 flex items-center gap-2">
              <Zap className="w-5 h-5 md:w-6 md:h-6 text-purple-400" />
              Token Allocation
            </h3>
            {allocations.map((allocation, index) => {
              const isHovered = hoveredIndex === index;
              const isSelected = selectedIndex === index;
              
              return (
                <div
                  key={index}
                  className={`p-3 md:p-4 rounded-lg border-2 transition-all duration-300 cursor-pointer ${
                    isHovered || isSelected
                      ? "border-purple-500 bg-purple-500/10 scale-105"
                      : "border-purple-500/20 bg-gray-900/50"
                  }`}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: allocation.color }}
                      />
                      <span className="font-semibold text-white">{allocation.label}</span>
                    </div>
                    <span className="text-lg font-bold text-purple-400">
                      {allocation.percentage}%
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 ml-7">{allocation.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
